import React, { createContext, useContext, useState, useCallback } from "react";
import Toast from "./Toast";

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

let toastCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "error", duration = 5000) => {
    const id = ++toastCounter;
    const toast = { id, message, type, duration };

    setToasts((prev) => [...prev, toast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showError = useCallback(
    (message, duration = 5000) => {
      return addToast(message, "error", duration);
    },
    [addToast]
  );

  const showSuccess = useCallback(
    (message, duration = 4000) => {
      return addToast(message, "success", duration);
    },
    [addToast]
  );

  const showWarning = useCallback(
    (message, duration = 4000) => {
      return addToast(message, "warning", duration);
    },
    [addToast]
  );

  const showInfo = useCallback(
    (message, duration = 4000) => {
      return addToast(message, "info", duration);
    },
    [addToast]
  );

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = {
    addToast,
    removeToast,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    clearAll,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="toast-container">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
